import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Empty, Field, NativeSelect, Segmented, errMsg } from '@/components/hr/shared'
import { useCurrency } from '@/contexts/CurrencyContext'
import { CYCLE_LABEL, CYCLE_SHORT, RETURNS_LABEL, dayLabel, parseMoney, periodLabel } from '@/lib/trade'
import { toastHelpers } from '@/lib/toast-helpers'
import { cn } from '@/lib/utils'
import type { TradeAccount, TradeAccountInput, TradeBillingCycle, TradeBranch, TradeReturnsMode } from '@/types/trade'
import { PriceTable } from './PricesTab'
import { tradeRole, useTradeAccounts } from './TradePage'

// The cafés the tenant supplies: billing cycle, payment terms (7 days unless
// changed), sold outright or sale-or-return, branches with their own gadi
// fare, and the client's own prices where they differ from the defaults.

interface AccountForm {
  name: string
  contact_name: string
  contact_phone: string
  contact_email: string
  billing_cycle: TradeBillingCycle
  payment_terms_days: string
  returns_mode: TradeReturnsMode
  default_fare: string
  notes: string
}

const EMPTY: AccountForm = {
  name: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  billing_cycle: 'semi_monthly',
  payment_terms_days: '7',
  returns_mode: 'none',
  default_fare: '0',
  notes: '',
}

function toForm(a: TradeAccount): AccountForm {
  return {
    name: a.name,
    contact_name: a.contact_name ?? '',
    contact_phone: a.contact_phone ?? '',
    contact_email: a.contact_email ?? '',
    billing_cycle: a.billing_cycle,
    payment_terms_days: String(a.payment_terms_days),
    returns_mode: a.returns_mode,
    default_fare: String(a.default_fare),
    notes: a.notes ?? '',
  }
}

function formError(v: AccountForm): string | null {
  if (!v.name.trim()) return 'Give the client a name'
  const t = Number(v.payment_terms_days)
  if (!Number.isInteger(t) || t < 0 || t > 365) return 'Payment terms are 0 to 365 days'
  const f = parseMoney(v.default_fare)
  if (f != null && Number.isNaN(f)) return 'The delivery charge must be a number'
  return null
}

function toInput(v: AccountForm): TradeAccountInput {
  return {
    name: v.name.trim(),
    contact_name: v.contact_name,
    contact_phone: v.contact_phone,
    contact_email: v.contact_email,
    billing_cycle: v.billing_cycle,
    payment_terms_days: Number(v.payment_terms_days),
    returns_mode: v.returns_mode,
    default_fare: parseMoney(v.default_fare) ?? 0,
    notes: v.notes,
  }
}

function AccountFields({ value, onChange, disabled }: { value: AccountForm; onChange: (v: AccountForm) => void; disabled?: boolean }) {
  const set = (k: keyof AccountForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => onChange({ ...value, [k]: e.target.value })
  return (
    <fieldset disabled={disabled} className="grid gap-3 sm:grid-cols-2">
      <Field label="Client name" className="sm:col-span-2">
        <Input value={value.name} onChange={set('name')} placeholder="e.g. Coffeemistry" maxLength={120} />
      </Field>
      <Field label="Contact person">
        <Input value={value.contact_name} onChange={set('contact_name')} />
      </Field>
      <Field label="Phone">
        <Input value={value.contact_phone} onChange={set('contact_phone')} inputMode="tel" />
      </Field>
      <Field label="Email for invoices" className="sm:col-span-2">
        <Input value={value.contact_email} onChange={set('contact_email')} type="email" />
      </Field>
      <Field label="Billing">
        <NativeSelect value={value.billing_cycle} onChange={set('billing_cycle')}>
          {(Object.keys(CYCLE_LABEL) as TradeBillingCycle[]).map((c) => (
            <option key={c} value={c}>
              {CYCLE_LABEL[c]}
            </option>
          ))}
        </NativeSelect>
      </Field>
      <Field label="Payment due (days after the invoice)">
        <Input value={value.payment_terms_days} onChange={set('payment_terms_days')} inputMode="numeric" />
      </Field>
      <Field label="Unsold items" hint="Sale or return: unsold items can come back and are credited">
        <NativeSelect value={value.returns_mode} onChange={set('returns_mode')}>
          {(Object.keys(RETURNS_LABEL) as TradeReturnsMode[]).map((r) => (
            <option key={r} value={r}>
              {RETURNS_LABEL[r]}
            </option>
          ))}
        </NativeSelect>
      </Field>
      <Field label="Usual delivery charge" hint="A branch can have its own">
        <Input value={value.default_fare} onChange={set('default_fare')} inputMode="decimal" />
      </Field>
      <Field label="Notes" className="sm:col-span-2">
        <Textarea value={value.notes} onChange={set('notes')} rows={2} />
      </Field>
    </fieldset>
  )
}

export function ClientsTab({ clientId, onClient }: { clientId?: string; onClient: (id: string | undefined) => void }) {
  const { formatCurrency } = useCurrency()
  const { canSetup } = tradeRole()
  const [status, setStatus] = useState<'open' | 'closed'>('open')
  const [adding, setAdding] = useState(false)
  const { accounts, isLoading } = useTradeAccounts(status)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented
          value={status}
          onChange={setStatus}
          options={[
            { id: 'open', label: 'Current' },
            { id: 'closed', label: 'Closed' },
          ]}
        />
        {canSetup && (
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add client
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : accounts.length === 0 ? (
        <Empty>{status === 'closed' ? 'No closed clients.' : 'No clients yet. Add the cafés and shops you supply.'}</Empty>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Client</th>
                <th className="px-4 py-2 font-medium">Billing</th>
                <th className="px-4 py-2 font-medium">Last delivery</th>
                <th className="px-4 py-2 text-right font-medium">This period</th>
                <th className="px-4 py-2 text-right font-medium">Not yet billed</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {accounts.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-muted/30" onClick={() => onClient(a.id)}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2 font-medium">
                      {a.name}
                      {a.status === 'suspended' && <span className="rounded-md bg-warning-surface px-1.5 py-0.5 text-xs font-medium text-warning-fg">On hold</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {a.branch_count} branch{a.branch_count === 1 ? '' : 'es'}
                      {a.price_count > 0 && ` · ${a.price_count} own price${a.price_count === 1 ? '' : 's'}`}
                      {a.draft_count > 0 && <span className="text-warning-fg"> · {a.draft_count} draft{a.draft_count === 1 ? '' : 's'}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {CYCLE_SHORT[a.billing_cycle]} · {a.payment_terms_days} days
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{a.last_delivery ? dayLabel(a.last_delivery) : '—'}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {formatCurrency(a.period_value)}
                    <div className="text-xs text-muted-foreground">{periodLabel(a.period_start, a.period_end)}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium tabular-nums">{formatCurrency(a.open_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddClientDialog open={adding} onClose={() => setAdding(false)} onAdded={(id) => onClient(id)} />
      <ClientSheet id={clientId} onClose={() => onClient(undefined)} />
    </div>
  )
}

function AddClientDialog({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded: (id: string) => void }) {
  const qc = useQueryClient()
  const [value, setValue] = useState(EMPTY)
  const [branch, setBranch] = useState('')
  useEffect(() => {
    if (open) {
      setValue(EMPTY)
      setBranch('')
    }
  }, [open])
  const error = formError(value)
  const add = useMutation({
    mutationFn: () => apiClient.tradeCreateAccount({ ...toInput(value), branches: branch.trim() ? [{ label: branch.trim() }] : undefined }),
    onSuccess: (res) => {
      toastHelpers.success('Client added')
      qc.invalidateQueries({ queryKey: ['trade'] })
      onClose()
      if (res.data) onAdded(res.data.id)
    },
    onError: (err) => toastHelpers.error('Couldn’t add the client', errMsg(err)),
  })
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add client</DialogTitle>
          <DialogDescription>A café or shop you supply on account. You can add more branches and its own prices next.</DialogDescription>
        </DialogHeader>
        <AccountFields value={value} onChange={setValue} />
        <Field label="First branch" hint="Where you deliver, e.g. “F-8”. Optional">
          <Input value={branch} onChange={(e) => setBranch(e.target.value)} maxLength={120} />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => add.mutate()} disabled={!!error || add.isPending} title={error ?? undefined}>
            Add client
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type SheetTab = 'details' | 'branches' | 'prices'

function ClientSheet({ id, onClose }: { id?: string; onClose: () => void }) {
  const [tab, setTab] = useState<SheetTab>('details')
  useEffect(() => setTab('details'), [id])
  const q = useQuery({ queryKey: ['trade', 'account', id], queryFn: () => apiClient.tradeGetAccount(id!), enabled: !!id })
  const a = q.data?.data as TradeAccount | undefined
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent size="lg">
        <SheetHeader>
          <SheetTitle>{a?.name ?? '…'}</SheetTitle>
          <SheetDescription>{a ? `${CYCLE_SHORT[a.billing_cycle]} · payment due in ${a.payment_terms_days} days · ${RETURNS_LABEL[a.returns_mode]}` : ''}</SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-4">
          <Segmented
            value={tab}
            onChange={setTab}
            options={[
              { id: 'details', label: 'Details' },
              { id: 'branches', label: `Branches${a ? ` (${a.branch_count})` : ''}` },
              { id: 'prices', label: 'Their prices' },
            ]}
          />
          {!a ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : tab === 'details' ? (
            <DetailsPane a={a} />
          ) : tab === 'branches' ? (
            <BranchesPane a={a} />
          ) : (
            <PriceTable accountId={a.id} />
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}

function DetailsPane({ a }: { a: TradeAccount }) {
  const qc = useQueryClient()
  const { canSetup } = tradeRole()
  const base = useMemo(() => toForm(a), [a])
  const [value, setValue] = useState(base)
  useEffect(() => setValue(base), [base])
  const dirty = JSON.stringify(value) !== JSON.stringify(base)
  const error = formError(value)
  const save = useMutation({
    mutationFn: (body: TradeAccountInput) => apiClient.tradeUpdateAccount(a.id, body),
    onSuccess: (res) => {
      toastHelpers.success(res.message || 'Saved')
      qc.invalidateQueries({ queryKey: ['trade'] })
    },
    onError: (err) => toastHelpers.error('Couldn’t save', errMsg(err)),
  })
  return (
    <div className="space-y-4">
      <AccountFields value={value} onChange={setValue} disabled={!canSetup} />
      {canSetup && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-4">
          <div className="flex gap-2">
            {a.status === 'active' && (
              <Button variant="outline" size="sm" onClick={() => save.mutate({ status: 'suspended' })} disabled={save.isPending}>
                Put on hold
              </Button>
            )}
            {a.status !== 'active' && (
              <Button variant="outline" size="sm" onClick={() => save.mutate({ status: 'active' })} disabled={save.isPending}>
                Make active
              </Button>
            )}
            {a.status !== 'closed' && (
              <Button variant="ghost" size="sm" className="text-danger-fg" onClick={() => save.mutate({ status: 'closed' })} disabled={save.isPending}>
                Close client
              </Button>
            )}
          </div>
          <Button onClick={() => save.mutate(toInput(value))} disabled={!dirty || !!error || save.isPending} title={error ?? undefined}>
            Save
          </Button>
        </div>
      )}
      {a.status === 'suspended' && <p className="text-xs text-muted-foreground">On hold: no new deliveries can be recorded for this client.</p>}
    </div>
  )
}

function BranchesPane({ a }: { a: TradeAccount }) {
  const { formatCurrency } = useCurrency()
  const { canSetup } = tradeRole()
  const [editing, setEditing] = useState<TradeBranch | 'new' | null>(null)
  const branches = a.branches ?? []
  return (
    <div className="space-y-3">
      {branches.length === 0 ? (
        <Empty>No branches yet. Add each address you deliver to.</Empty>
      ) : (
        <div className="divide-y rounded-lg border">
          {branches.map((b) => (
            <div key={b.id} className={cn('flex items-start justify-between gap-3 px-4 py-3', !b.is_active && 'opacity-60')}>
              <div className="min-w-0">
                <div className="font-medium">
                  {b.label}
                  {!b.is_active && <span className="ml-2 text-xs font-normal text-muted-foreground">Switched off</span>}
                </div>
                {b.address && <div className="text-xs text-muted-foreground">{b.address}</div>}
                <div className="text-xs text-muted-foreground">
                  Delivery charge: {b.default_fare != null ? formatCurrency(b.default_fare) : `client’s usual (${formatCurrency(a.default_fare)})`}
                  {b.aliases.length > 0 && ` · also written as ${b.aliases.map((x) => `“${x}”`).join(', ')}`}
                </div>
              </div>
              {canSetup && (
                <Button variant="ghost" size="sm" onClick={() => setEditing(b)}>
                  Edit
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {canSetup && (
        <Button variant="outline" size="sm" onClick={() => setEditing('new')}>
          <Plus className="mr-1.5 h-4 w-4" />
          Add branch
        </Button>
      )}
      <BranchDialog account={a} branch={editing} onClose={() => setEditing(null)} />
    </div>
  )
}

function BranchDialog({ account, branch, onClose }: { account: TradeAccount; branch: TradeBranch | 'new' | null; onClose: () => void }) {
  const qc = useQueryClient()
  const b = branch && branch !== 'new' ? branch : null
  const [label, setLabel] = useState('')
  const [address, setAddress] = useState('')
  const [fare, setFare] = useState('')
  const [aliases, setAliases] = useState('')
  useEffect(() => {
    setLabel(b?.label ?? '')
    setAddress(b?.address ?? '')
    setFare(b?.default_fare != null ? String(b.default_fare) : '')
    setAliases(b?.aliases.join(', ') ?? '')
  }, [branch]) // eslint-disable-line react-hooks/exhaustive-deps
  const fareNum = parseMoney(fare)
  const invalid = !label.trim() || (fareNum != null && Number.isNaN(fareNum))
  const body = () => ({
    label: label.trim(),
    address,
    aliases: aliases.split(',').map((s) => s.trim()).filter(Boolean),
    ...(fareNum == null ? { clear_fare: true } : { default_fare: fareNum }),
  })
  const save = useMutation({
    mutationFn: () => (b ? apiClient.tradeUpdateBranch(b.id, body()) : apiClient.tradeCreateBranch(account.id, body())),
    onSuccess: (res) => {
      toastHelpers.success(res.message || 'Saved')
      qc.invalidateQueries({ queryKey: ['trade'] })
      onClose()
    },
    onError: (err) => toastHelpers.error('Couldn’t save the branch', errMsg(err)),
  })
  const toggle = useMutation({
    mutationFn: () => apiClient.tradeUpdateBranch(b!.id, { is_active: !b!.is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trade'] })
      onClose()
    },
    onError: (err) => toastHelpers.error('Couldn’t update the branch', errMsg(err)),
  })
  return (
    <Dialog open={!!branch} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{b ? `Edit ${b.label}` : `Add a branch to ${account.name}`}</DialogTitle>
          <DialogDescription>Each address you deliver to.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Branch name">
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. F-8" maxLength={120} />
          </Field>
          <Field label="Address" hint="Optional">
            <Textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} />
          </Field>
          <Field label="Delivery charge for this branch" hint={`Leave empty to use the client’s usual charge (${account.default_fare})`}>
            <Input value={fare} onChange={(e) => setFare(e.target.value)} inputMode="decimal" />
          </Field>
          <Field label="Also written as" hint="How your team writes this address in WhatsApp, separated by commas — e.g. “coffeemistry F-8”">
            <Input value={aliases} onChange={(e) => setAliases(e.target.value)} />
          </Field>
        </div>
        <DialogFooter className="sm:justify-between">
          {b ? (
            <Button variant="ghost" onClick={() => toggle.mutate()} disabled={toggle.isPending}>
              {b.is_active ? 'Switch off' : 'Switch on'}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} disabled={invalid || save.isPending}>
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
